import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { UserInfo, LoginRequest, RegisterRequest, GoogleSignInRequest, AppleSignInRequest, UpdateProfileRequest, InitiateAccountDeletionRequest, AccountDeletionResponse, CancelDeletionResponse, AccountDeletionStatusResponse, ResetPasswordRequest, PasswordResetResponse } from '../types';
import { authApi, setUnauthorizedCallback } from '../services/authApi';
import { setHabitApiUnauthorizedCallback } from '../services/habitApi';
import { setSubscriptionApiUnauthorizedCallback } from '../services/subscriptionApi';
import { setSupportApiUnauthorizedCallback } from '../services/supportApi';
import toast from 'react-hot-toast';
import { detectUserTimezone, normalizeTimezone } from '../utils/timezone';

interface AuthState {
  // State
  user: UserInfo | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  hasDismissedTimezonePrompt?: boolean;

  // Actions
  login: (credentials: LoginRequest) => Promise<boolean>;
  register: (userData: RegisterRequest) => Promise<boolean>;
  googleSignIn: (googleData: GoogleSignInRequest) => Promise<boolean>;
  appleSignIn: (appleData: AppleSignInRequest) => Promise<boolean>;
  updateProfile: (profileData: UpdateProfileRequest) => Promise<boolean>;
  completeFirstLoginSetup: (timezone: string) => Promise<boolean>;
  logout: () => void;
  silentLogout: () => void;
  getCurrentUser: () => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  dismissTimezonePrompt: () => void;
  resetTimezonePrompt: () => void;
  
  // Token management
  setToken: (token: string | null) => void;
  getAuthHeader: () => string | null;
  isTokenExpired: () => boolean;
  
  // Account deletion
  deleteAccount: (data: InitiateAccountDeletionRequest) => Promise<AccountDeletionResponse>;
  cancelAccountDeletion: () => Promise<CancelDeletionResponse>;
  getAccountDeletionStatus: () => Promise<AccountDeletionStatusResponse>;
  
  // Password reset
  forgotPassword: (email: string) => Promise<PasswordResetResponse>;
  resetPassword: (data: ResetPasswordRequest) => Promise<PasswordResetResponse>;
  resendPasswordResetOtp: (email: string) => Promise<PasswordResetResponse>;
}

// Helper function to check if token is expired
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Error parsing token:', error);
    return true; // Treat as expired if we can't parse it
  }
};

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        hasDismissedTimezonePrompt: false,

        // Actions
        login: async (credentials: LoginRequest) => {
          set({ isLoading: true, error: null });
          
          try {
            const response = await authApi.login(credentials);
            
            if (response.success && response.token && response.user) {
              set({
                user: response.user,
                token: response.token,
                isAuthenticated: true,
                isLoading: false,
                error: null,
              });
              
              // Load subscription data after successful login
              const subscriptionStore = await import('../store/subscriptionStore');
              subscriptionStore.useSubscriptionStore.getState().loadFeatureAccess();
              subscriptionStore.useSubscriptionStore.getState().loadSubscription();
              
              toast.success('Login successful!');
              return true;
            } else {
              set({
                error: response.message || 'Login failed',
                isLoading: false,
              });
              toast.error(response.message || 'Login failed');
              return false;
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Login failed';
            set({
              error: errorMessage,
              isLoading: false,
            });
            toast.error(errorMessage);
            return false;
          }
        },

        register: async (userData: RegisterRequest) => {
          set({ isLoading: true, error: null });
          
          try {
            // Auto-detect timezone if not provided
            if (!userData.timeZone) {
              userData.timeZone = normalizeTimezone(detectUserTimezone());
            }
            const response = await authApi.register(userData);
            
            if (response.success && response.token && response.user) {
              set({
                user: response.user,
                token: response.token,
                isAuthenticated: true,
                isLoading: false,
                error: null,
                hasDismissedTimezonePrompt: true, // new users already set tz
              });
              
              toast.success('Registration successful!');
              return true;
            } else {
              set({
                error: response.message || 'Registration failed',
                isLoading: false,
              });
              toast.error(response.message || 'Registration failed');
              return false;
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Registration failed';
            set({
              error: errorMessage,
              isLoading: false,
            });
            toast.error(errorMessage);
            return false;
          }
        },

        googleSignIn: async (googleData: GoogleSignInRequest) => {
          set({ isLoading: true, error: null });
          
          try {
            const response = await authApi.googleSignIn(googleData);
            
            if (response.success && response.token && response.user) {
              set({
                user: response.user,
                token: response.token,
                isAuthenticated: true,
                isLoading: false,
                error: null,
              });
              
              toast.success('Google sign-in successful!');
              return true;
            } else {
              set({
                error: response.message || 'Google sign-in failed',
                isLoading: false,
              });
              toast.error(response.message || 'Google sign-in failed');
              return false;
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Google sign-in failed';
            set({
              error: errorMessage,
              isLoading: false,
            });
            toast.error(errorMessage);
            return false;
          }
        },

        appleSignIn: async (appleData: AppleSignInRequest) => {
          set({ isLoading: true, error: null });
          
          try {
            const response = await authApi.appleSignIn(appleData);
            
            if (response.success && response.token && response.user) {
              set({
                user: response.user,
                token: response.token,
                isAuthenticated: true,
                isLoading: false,
                error: null,
              });
              
              // Load subscription data after successful login
              const subscriptionStore = await import('../store/subscriptionStore');
              subscriptionStore.useSubscriptionStore.getState().loadFeatureAccess();
              subscriptionStore.useSubscriptionStore.getState().loadSubscription();
              
              toast.success('Apple sign-in successful!');
              return true;
            } else {
              set({
                error: response.message || 'Apple sign-in failed',
                isLoading: false,
              });
              toast.error(response.message || 'Apple sign-in failed');
              return false;
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Apple sign-in failed';
            set({
              error: errorMessage,
              isLoading: false,
            });
            toast.error(errorMessage);
            return false;
          }
        },

        updateProfile: async (profileData: UpdateProfileRequest) => {
          set({ isLoading: true, error: null });

          try {
            const updatedUser = await authApi.updateProfile(profileData);

            set({
              user: updatedUser,
              isLoading: false,
              error: null,
            });

            // If timezone was updated, no need to prompt anymore
            if (profileData.timeZone) {
              set({ hasDismissedTimezonePrompt: true });
            }

            toast.success('Profile updated successfully!');
            return true;
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Profile update failed';
            set({
              error: errorMessage,
              isLoading: false,
            });
            toast.error(errorMessage);
            return false;
          }
        },

        completeFirstLoginSetup: async (timezone: string) => {
          set({ isLoading: true, error: null });

          try {
            // Update profile with timezone and mark first login setup as complete
            const profileData: UpdateProfileRequest = {
              timeZone: normalizeTimezone(timezone),
              hasCompletedFirstLoginSetup: true
            };

            const updatedUser = await authApi.updateProfile(profileData);

            set({
              user: updatedUser,
              isLoading: false,
              error: null,
              hasDismissedTimezonePrompt: true, // No longer need to prompt
            });

            toast.success('Welcome! Your timezone has been set.');
            return true;
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to complete setup';
            set({
              error: errorMessage,
              isLoading: false,
            });
            toast.error(errorMessage);
            return false;
          }
        },

        logout: () => {
          // Only call logout API endpoint if we have a valid token
          const { token } = get();
          if (token && !get().isTokenExpired()) {
            authApi.logout().catch(console.error);
          }
          
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            error: null,
          });
          
          // Reset subscription store on logout
          import('../store/subscriptionStore').then(subscriptionStore => {
            subscriptionStore.useSubscriptionStore.getState().reset();
          });
          
          toast.success('Logged out successfully');
        },

        silentLogout: () => {
          // Silent logout for expired tokens - no API call
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            error: null,
            isLoading: false,
          });
          
          // Show a toast message to inform the user
          toast.error('Your session has expired. Please log in again.');
        },

        getCurrentUser: async () => {
          const { token } = get();
          
          // If no token, don't make the request
          if (!token) {
            set({ isLoading: false });
            return;
          }

          // If token is expired, clear auth state without API call
          if (get().isTokenExpired()) {
            console.warn('Token expired, clearing auth state');
            get().silentLogout();
            return;
          }

          set({ isLoading: true });
          
          try {
            const user = await authApi.getCurrentUser();
            set({
              user,
              isLoading: false,
            });
          } catch (error) {
            console.error('Failed to get current user:', error);
            // Clear auth state silently for token issues
            get().silentLogout();
          }
        },

        clearError: () => set({ error: null }),

        setLoading: (isLoading: boolean) => set({ isLoading }),

        dismissTimezonePrompt: () => set({ hasDismissedTimezonePrompt: true }),
        resetTimezonePrompt: () => set({ hasDismissedTimezonePrompt: false }),

        // Token management
        setToken: (token: string | null) => {
          set({ token, isAuthenticated: !!token });
        },

        getAuthHeader: () => {
          const { token } = get();
          return token ? `Bearer ${token}` : null;
        },

        isTokenExpired: () => {
          const { token } = get();
          return !token || isTokenExpired(token);
        },

        // Account deletion methods
        deleteAccount: async (data: InitiateAccountDeletionRequest): Promise<AccountDeletionResponse> => {
          set({ isLoading: true, error: null });
          
          try {
            const response = await authApi.deleteAccount(data);
            
            if (response.success) {
              // Account deletion initiated successfully - logout immediately
              toast.success(response.message);
              get().logout();
            } else {
              set({ error: response.message });
              toast.error(response.message);
            }
            
            return response;
          } catch (error: any) {
            const errorMessage = error.message || 'Failed to delete account';
            set({ error: errorMessage });
            toast.error(errorMessage);
            throw error;
          } finally {
            set({ isLoading: false });
          }
        },

        cancelAccountDeletion: async (): Promise<CancelDeletionResponse> => {
          set({ isLoading: true, error: null });
          
          try {
            const response = await authApi.cancelAccountDeletion();
            
            if (response.success) {
              toast.success(response.message);
              // Refresh user data to reflect the cancellation
              await get().getCurrentUser();
            } else {
              set({ error: response.message });
              toast.error(response.message);
            }
            
            return response;
          } catch (error: any) {
            const errorMessage = error.message || 'Failed to cancel account deletion';
            set({ error: errorMessage });
            toast.error(errorMessage);
            throw error;
          } finally {
            set({ isLoading: false });
          }
        },

        getAccountDeletionStatus: async (): Promise<AccountDeletionStatusResponse> => {
          try {
            const response = await authApi.getAccountDeletionStatus();
            return response;
          } catch (error: any) {
            console.error('Failed to get account deletion status:', error);
            throw error;
          }
        },

        // Password reset methods
        forgotPassword: async (email: string): Promise<PasswordResetResponse> => {
          set({ error: null });
          
          try {
            const response = await authApi.forgotPassword({ email });
            return response;
          } catch (error: any) {
            const errorMessage = error.message || 'Failed to send password reset code';
            set({ error: errorMessage });
            throw error;
          }
        },

        resetPassword: async (data: ResetPasswordRequest): Promise<PasswordResetResponse> => {
          set({ error: null });
          
          try {
            const response = await authApi.resetPassword(data);
            return response;
          } catch (error: any) {
            const errorMessage = error.message || 'Failed to reset password';
            set({ error: errorMessage });
            throw error;
          }
        },

        resendPasswordResetOtp: async (email: string): Promise<PasswordResetResponse> => {
          set({ error: null });
          
          try {
            const response = await authApi.resendPasswordResetOtp({ email });
            return response;
          } catch (error: any) {
            const errorMessage = error.message || 'Failed to resend password reset code';
            set({ error: errorMessage });
            throw error;
          }
        },
      }),
      {
        name: 'auth-store',
        partialize: (state) => ({
          user: state.user,
          token: state.token,
          isAuthenticated: state.isAuthenticated,
          hasDismissedTimezonePrompt: state.hasDismissedTimezonePrompt,
        }),
      }
    ),
    { name: 'auth-store' }
  )
);

// Set up the unauthorized callback when the store is created
setUnauthorizedCallback(() => {
  const store = useAuthStore.getState();
  store.silentLogout();
});

// Also set up the callback for habitApi
setHabitApiUnauthorizedCallback(() => {
  const store = useAuthStore.getState();
  store.silentLogout();
});

// Set up the callback for subscriptionApi
setSubscriptionApiUnauthorizedCallback(() => {
  const store = useAuthStore.getState();
  store.silentLogout();
});

// Set up the callback for supportApi
setSupportApiUnauthorizedCallback(() => {
  const store = useAuthStore.getState();
  store.silentLogout();
}); 